import { create } from 'zustand';
import { useCallback, useEffect } from 'react';

export type BadgeType = 'attachments' | 'category' | 'installments' | 'scheduled';

export interface BadgeUpdateEvent {
  transactionId: string;
  type: BadgeType;
  action: 'add' | 'remove' | 'update';
  data?: Record<string, unknown>;
  timestamp: number;
}

export interface BadgeState {
  // Estado otimista dos badges por transação
  [transactionId: string]: {
    hasAttachments: boolean;
    hasCategory: boolean;
    hasInstallments: boolean;
    isScheduled: boolean;
    lastUpdated: number;
  };

  // Fila de eventos para processamento em batch
  eventQueue: BadgeUpdateEvent[];

  // Timestamp da última atualização global
  lastGlobalUpdate: number;
}

interface BadgeActions {
  // Atualizar estado otimista de um badge específico
  updateBadge: (transactionId: string, type: BadgeType, hasBadge: boolean, data?: Record<string, unknown>) => void;

  // Processar eventos em fila (batch updates)
  processEventQueue: () => void;

  // Adicionar evento à fila
  addEventToQueue: (event: BadgeUpdateEvent) => void;

  // Invalidar cache específico de uma transação
  invalidateTransaction: (transactionId: string) => void;

  // Limpar cache de transações antigas
  cleanup: () => void;

  // Reset completo (para testes)
  reset: () => void;
}

// Estado global de badges usando Zustand para performance
export const useBadgeStore = create<BadgeState & BadgeActions>()((set, get) => ({
  // Estado inicial - usar spread operator para inicializar o objeto corretamente
  eventQueue: [],
  lastGlobalUpdate: Date.now(),

  // Atualizar estado otimista de um badge
  updateBadge: (transactionId: string, type: BadgeType, hasBadge: boolean, data?: Record<string, unknown>) => {
    set((state) => {
      const currentState = state[transactionId] || {
        hasAttachments: false,
        hasCategory: false,
        hasInstallments: false,
        isScheduled: false,
        lastUpdated: 0,
      };

      const updatedState = {
        ...currentState,
        [type === 'attachments' ? 'hasAttachments' :
         type === 'category' ? 'hasCategory' :
         type === 'installments' ? 'hasInstallments' :
         'isScheduled']: hasBadge,
        lastUpdated: Date.now(),
      };

      return {
        ...state,
        [transactionId]: updatedState,
      };
    });
  },

  // Processar eventos em fila com debounce
  processEventQueue: () => {
    const { eventQueue, updateBadge } = get();

    if (eventQueue.length === 0) return;

    // Processar eventos em batch para performance
    const eventsByTransaction = eventQueue.reduce((acc, event) => {
      if (!acc[event.transactionId]) {
        acc[event.transactionId] = [];
      }
      acc[event.transactionId].push(event);
      return acc;
    }, {} as Record<string, BadgeUpdateEvent[]>);

    // Processar cada transação
    Object.entries(eventsByTransaction).forEach(([transactionId, events]) => {
      events.forEach(event => {
        const hasBadge = event.action === 'add' || event.action === 'update';
        updateBadge(transactionId, event.type, hasBadge, event.data);
      });
    });

    // Limpar fila após processamento
    set({ eventQueue: [] });
  },

  // Adicionar evento à fila com validação
  addEventToQueue: (event: BadgeUpdateEvent) => {
    // Validar evento antes de adicionar
    if (!event.transactionId || !event.type) {
      console.warn('Invalid badge event:', event);
      return;
    }

    set((state) => ({
      ...state,
      eventQueue: [...state.eventQueue, event],
    }));

    // Processar eventos em batch (debounce de 50ms para performance)
    setTimeout(() => {
      get().processEventQueue();
    }, 50);
  },

  // Invalidar cache específico
  invalidateTransaction: (transactionId: string) => {
    set((state) => {
      const newState = { ...state };
      delete newState[transactionId];
      return newState;
    });
  },

  // Limpar cache antigo (manter apenas últimos 5 minutos)
  cleanup: () => {
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

    set((state) => {
      const newState: Record<string, unknown> = {
        eventQueue: state.eventQueue,
        lastGlobalUpdate: state.lastGlobalUpdate,
      };

      Object.entries(state).forEach(([key, value]) => {
        if (key !== 'eventQueue' && key !== 'lastGlobalUpdate' && typeof value === 'object' && value !== null) {
          const badge = value as { lastUpdated: number };
          if (badge.lastUpdated && badge.lastUpdated > fiveMinutesAgo) {
            newState[key] = value;
          }
        }
      });

      return newState as BadgeState & BadgeActions;
    });
  },

  // Reset completo (para testes)
  reset: () => {
    set({
      eventQueue: [],
      lastGlobalUpdate: Date.now(),
    } as BadgeState & BadgeActions);
  },
}));

// Hook principal para sincronização em tempo real
export function useRealtimeBadgeSync(transactionId: string) {
  // Acessa o estado do badge diretamente pelo transactionId
  // Retorna undefined se não existir (transação nova/temporária)
  const badgeState = useBadgeStore(
    (state) => transactionId ? state[transactionId] : undefined
  );

  const {
    updateBadge,
    addEventToQueue,
    invalidateTransaction,
  } = useBadgeStore();

  // Funções de atualização otimista
  const updateAttachments = useCallback((hasAttachments: boolean) => {
    addEventToQueue({
      transactionId,
      type: 'attachments',
      action: hasAttachments ? 'add' : 'remove',
      timestamp: Date.now(),
    });
  }, [transactionId, addEventToQueue]);

  const updateCategory = useCallback((hasCategory: boolean, categoryData?: Record<string, unknown>) => {
    addEventToQueue({
      transactionId,
      type: 'category',
      action: hasCategory ? 'add' : 'remove',
      data: categoryData,
      timestamp: Date.now(),
    });
  }, [transactionId, addEventToQueue]);

  const updateInstallments = useCallback((hasInstallments: boolean, installmentData?: Record<string, unknown>) => {
    addEventToQueue({
      transactionId,
      type: 'installments',
      action: hasInstallments ? 'add' : 'remove',
      data: installmentData,
      timestamp: Date.now(),
    });
  }, [transactionId, addEventToQueue]);

  const updateScheduled = useCallback((isScheduled: boolean) => {
    addEventToQueue({
      transactionId,
      type: 'scheduled',
      action: isScheduled ? 'add' : 'remove',
      timestamp: Date.now(),
    });
  }, [transactionId, addEventToQueue]);

  // Função para atualizar múltiplos badges de uma vez
  const updateMultipleBadges = useCallback((updates: {
    hasAttachments?: boolean;
    hasCategory?: boolean;
    hasInstallments?: boolean;
    isScheduled?: boolean;
  }) => {
    const events: BadgeUpdateEvent[] = [];

    if ('hasAttachments' in updates) {
      events.push({
        transactionId,
        type: 'attachments',
        action: updates.hasAttachments ? 'add' : 'remove',
        timestamp: Date.now(),
      });
    }

    if ('hasCategory' in updates) {
      events.push({
        transactionId,
        type: 'category',
        action: updates.hasCategory ? 'add' : 'remove',
        timestamp: Date.now(),
      });
    }

    if ('hasInstallments' in updates) {
      events.push({
        transactionId,
        type: 'installments',
        action: updates.hasInstallments ? 'add' : 'remove',
        timestamp: Date.now(),
      });
    }

    if ('isScheduled' in updates) {
      events.push({
        transactionId,
        type: 'scheduled',
        action: updates.isScheduled ? 'add' : 'remove',
        timestamp: Date.now(),
      });
    }

    // Adicionar todos eventos à fila de uma vez
    events.forEach(event => addEventToQueue(event));
  }, [transactionId, addEventToQueue]);

  return {
    // Estado otimista atual
    badgeState,

    // Funções de atualização
    updateAttachments,
    updateCategory,
    updateInstallments,
    updateScheduled,
    updateMultipleBadges,

    // Funções de controle
    invalidateTransaction,
  };
}

// Hook para limpeza automática de cache
export function useBadgeCacheCleanup() {
  const { cleanup } = useBadgeStore();

  useEffect(() => {
    // Limpar cache a cada 2 minutos
    const interval = setInterval(() => {
      cleanup();
    }, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [cleanup]);
}

// Hook para escutar eventos globais de atualização de badges
export function useBadgeEventListener() {
  const { updateMultipleBadges, invalidateTransaction } = useBadgeStore();

  useEffect(() => {
    const handleBadgeUpdate = (event: CustomEvent) => {
      const { transactionId, updates, deleted } = event.detail;

      if (deleted) {
        // Se a transação foi excluída, remover do store
        invalidateTransaction(transactionId);
      } else {
        // Atualizar badges com base nos dados recebidos
        updateMultipleBadges(transactionId, updates);
      }
    };

    // Adicionar event listener
    window.addEventListener('transaction-badge-update', handleBadgeUpdate as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('transaction-badge-update', handleBadgeUpdate as EventListener);
    };
  }, [updateMultipleBadges, invalidateTransaction]);
}